/**
 * WIPEOUT entity → `RenderableEntity` adapter (PR-D, LEGACY string-bag path).
 *
 * AutoCAD's WIPEOUT is a closed polygon with an opaque white fill that
 * masks geometry beneath it (DXF `AcDbWipeout` extends `AcDbRasterImage`
 * — see drawing-model wipeoutEntitySchema). Our renderer treats it as
 * a flat `THREE.Mesh` with a white `MeshBasicMaterial`, drawn between
 * the hatch and line passes so it covers hatches but is itself covered
 * by line strokes (matches AutoCAD's draw order: HATCH < WIPEOUT <
 * LINES per AutoCAD's documented z-stack).
 *
 * # Vertex source
 *
 * `buildWipeoutPreview` already exposes vertices keyed `Vertex 1 X` /
 * `Vertex 1 Y` / … / `Vertex N X` / `Vertex N Y`. We read until the
 * first non-finite coordinate; this matches the SVG renderer's
 * `renderWipeout` exactly.
 *
 * DEFUSE Direction-3 — this legacy adapter now extracts the raw corners
 * from the `EntityPreview.properties` string bag, then delegates the
 * geometry build + collinearity reject + AABB to the shared pure
 * `buildWipeoutSceneGeometry` (`./geom/wipeoutGeometry.ts`). The typed
 * sibling (`./typed/wipeoutFromEntity.ts`) calls the SAME builder with
 * corners read off the canonical model, so the two paths share one
 * geometry truth (no typed↔legacy clone).
 *
 * # Edge cases (now centralised in the shared builder)
 *
 *   - Vertex count < 3 → return null (renderer skips).
 *   - Non-finite vertex coordinate in the bag → return null (bag-specific
 *     concern; kept in this extraction since the typed model guarantees
 *     finite numbers via zod).
 *   - All corners collinear → return null (the shared builder's signed-area
 *     check rejects slivers so earcut never produces a degenerate mesh).
 *   - Frame off → renderer suppresses the boundary stroke; the mesh
 *     still draws.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import type { RenderableEntity, Vec3 } from "../types";

import { buildRenderableShell, type AdapterContext } from "./adapterContext";
import { buildWipeoutSceneGeometry } from "./geom/wipeoutGeometry";

export function wipeoutToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity | null {
  const vertexCount = Number(entity.properties["Vertex count"] ?? "0");
  if (!Number.isFinite(vertexCount) || vertexCount < 3) {
    return null;
  }
  const corners: Vec3[] = [];
  for (let i = 1; i <= vertexCount; i++) {
    const x = Number(entity.properties[`Vertex ${i} X`]);
    const y = Number(entity.properties[`Vertex ${i} Y`]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return null;
    }
    // Legacy bag drops z (`buildWipeoutPreview` emits only X/Y) → z: 0.
    corners.push({ x, y, z: 0 });
  }

  // Shared one-truth: applyOffset per corner, collinearity reject, geometry +
  // AABB. Returns null when the contour cannot paint an interior.
  const result = buildWipeoutSceneGeometry(corners);
  if (!result) {
    return null;
  }

  return buildRenderableShell(entity, ctx, result.geometry, result.bounds);
}
